// POLITILOGGEN — første faktiske kildeadapter i grunnbildet.
//
// ── HVORFOR DENNE KILDEN FØRST ──────────────────────────────────────────────
// `HENDELSESKART_PLAN.md` (målt mot live-API 2026-07-31) rangerer den øverst av
// gode grunner, og den er den eneste kilden i `kilderegister.ts` med **verifisert
// lisens** (NLOD 2.0). Ingen autentisering, 15 kontrollerte kategorier satt av
// politiet selv, og 300/300 riktig kommune i målingen. Eier spurte dessuten
// direkte: «Får vi koblet politiloggen rett inn i varsler?»
//
// ⚠ Planen sier «Status: plan. Ingen kode.» Det som FINNES er `kommunenr.ts` (357
// kommuner, tvetydighet håndtert) — bygget for nettopp denne adapteren, med NULL
// kallsteder til nå. Denne fila er der den tas i bruk.
//
// ── ⚠ TRE TING SOM MÅ VÆRE RIKTIGE, ELLERS LYVER KARTET ─────────────────────
//
// 1. POLITILOGGEN HAR INGEN KOORDINATER. Feltene er `district`, `municipality` og
//    `area` — det siste er fritekst («ved Katten», «E18 sørgående»). Å sette
//    kommunesenteret som posisjon og tegne det som en pin er den ene feilen som
//    skiller et ærlig hendelseskart fra et som lyver: en operatør ser et kryss i
//    Horten sentrum og tror politiet var der. Derfor bærer hvert objekt et
//    PRESISJONSNIVÅ, og geokodingen skjer i hubben — denne fila lager objektet
//    UTEN posisjon når den ikke har en ærlig en.
//
// 2. `Removed` ER EN MELDINGSTYPE. Politiet TREKKER meldinger tilbake. En
//    tilbaketrukket politimelding som står igjen på kartet er ikke gammel
//    informasjon — den er FEILINFORMASJON vi selv holder liv i. `erTrukket()`
//    finnes for at kalleren skal kunne fjerne den, ikke bare merke den.
//
// 3. SAMMENDRAGET SKAL IKKE OMSKRIVES. Planen er kategorisk, og begrunnelsen er
//    god: politiets `text` er allerede menneskeskrevet og ekstraktivt. Kjører vi
//    det gjennom en modell, innfører vi hallusinasjonsrisiko i et faktafelt som
//    var korrekt før vi rørte det — og gjør OSS til avsender av påstanden. Skriver
//    sammendraget «to omkom» der originalen skrev «ingen skadd», er det VÅR
//    faktafeil på VÅRT kart. Vi trunkerer på setningsgrense og lenker ut.
import { z } from 'zod';
import { kommunenrFor, kommunenavnFor } from './kommunenr.js';
import { districtForKommuneNr } from './hv-structure.js';
import { GrunnbildeObjektSchema, PRESISJON_LABEL, presisjonsForbehold, } from './grunnbilde.js';
// ── Kildeformatet ───────────────────────────────────────────────────────────
/**
 * Politiloggens meldingstyper.
 *
 * ⚠ `Removed` er grunnen til at dette er en enum og ikke en ignorert streng. Se
 * filhodet punkt 2.
 */
export const PolitiMeldingstypeSchema = z.enum(['Message', 'Created', 'Updated', 'Removed']);
/**
 * Én melding fra `api.politiloggen.politiet.no`.
 *
 * ⚠ ALT ER VALGFRITT UNNTATT ID OG TID. Feltene er observert utfylt i praksis
 * (`area` 89 %, `municipality` 300/300), men et skjema som KREVER dem ville
 * kastet en hel batch fordi én melding manglet område. En hendelse uten område er
 * fortsatt en hendelse — den får bare dårligere presisjon.
 */
export const PolitiMeldingSchema = z.object({
    id: z.string().max(128),
    threadId: z.string().max(128).optional(),
    /** Publiseringstidspunkt (ISO). */
    createdOn: z.string().max(64),
    /** Sist endret (ISO). Brukes til å oppdage rettelser. */
    updatedOn: z.string().max(64).optional(),
    messageType: PolitiMeldingstypeSchema.default('Message'),
    /** Politiets egen kategori — én av de 15. */
    category: z.string().max(64).default(''),
    /** Politidistrikt («Øst politidistrikt»). Skiller tvetydige kommunenavn. */
    district: z.string().max(120).default(''),
    municipality: z.string().max(120).default(''),
    /** FRITEKST-område («ved Katten», «E18 sørgående»). Grunnlaget for geokoding. */
    area: z.string().max(200).default(''),
    /** Meldingsteksten, menneskeskrevet av politiet. */
    text: z.string().max(8000).default(''),
    isActive: z.boolean().optional(),
});
/**
 * Parser en batch trygt. Ugyldige meldinger DROPPES, ikke kastes.
 *
 * ⚠ RETURNERER OGSÅ ANTALL FORKASTEDE. En stille dropping ville gitt et
 * grunnbilde med hull ingen kunne se — og hvis politiet endrer formatet, er det
 * nettopp dette tallet som avslører det.
 */
export function parsePolitiBatch(raa) {
    const arr = Array.isArray(raa)
        ? raa
        // ⚠ `messages` er den FAKTISKE toppnøkkelen fra `/messages` (verifisert mot
        // live API 2026-08-19 — se hub-fila for hvorfor det er endepunktet vi bruker).
        // `messageThreads` og `data` beholdes for bakoverkompatibilitet/tester.
        : raa?.data
            ?? raa?.messages
            ?? raa?.messageThreads;
    if (!Array.isArray(arr))
        return { meldinger: [], forkastet: 0 };
    const ut = [];
    let forkastet = 0;
    for (const m of arr.slice(0, 5000)) {
        const r = PolitiMeldingSchema.safeParse(m);
        if (r.success)
            ut.push(r.data);
        else
            forkastet++;
    }
    return { meldinger: ut, forkastet };
}
/** Er meldingen trukket tilbake av politiet? Se filhodet punkt 2. */
export function erTrukket(m) {
    return m.messageType === 'Removed' || m.isActive === false;
}
// ── Kategorier: politiets 15 + tre ─────────────────────────────────────────
/**
 * Politiets egne 15 kategorier, pluss tre vi legger til og stopper der.
 *
 * ⚠ VI OPPFINNER IKKE EGNE. Planen er tydelig: dette er politiets autoritative
 * operative inndeling, 15 er riktig antall (nok til å filtrere, få nok til en
 * legende som ikke drukner kartet), og den har allerede en ærlig restkategori
 * («Andre hendelser»). De tre tilleggene dekker kilder politiet ikke har:
 * `Naturfare` (NVE/MET), `Infrastruktur` (Vegvesen), `Sikkerhet` (vårt eget
 * domene).
 */
export const HENDELSESKATEGORIER = [
    'Arrangement', 'Brann', 'Dyr', 'Innbrudd', 'Redning', 'Ro og orden', 'Savnet',
    'Sjø', 'Skadeverk', 'Trafikk', 'Tyveri', 'Ulykke', 'Voldshendelse', 'Vær',
    'Andre hendelser',
    // Tilleggene — for kilder politiet ikke dekker.
    'Naturfare', 'Infrastruktur', 'Sikkerhet',
];
/** Politiets 15 alene — de vi ikke har oppfunnet. */
export const POLITI_KATEGORIER = HENDELSESKATEGORIER.slice(0, 15);
/**
 * Kildens kategoristreng → vår kategori.
 *
 * ⚠ UKJENT KATEGORI BLIR «Andre hendelser», IKKE FORKASTET. Legger politiet til
 * en kategori vi ikke kjenner, skal hendelsen fortsatt komme fram — men den skal
 * ikke bli tildelt en kategori vi gjetter oss til, for kategorien styrer både
 * filter og alvorlighet.
 */
export function kategoriFor(raa) {
    const n = raa.trim().toLowerCase();
    const treff = HENDELSESKATEGORIER.find((k) => k.toLowerCase() === n);
    return treff ?? 'Andre hendelser';
}
/** Basisnivå per kategori (planen §3). */
const KATEGORI_BASIS = {
    Voldshendelse: 3, Savnet: 3, Redning: 3,
    Brann: 2, Ulykke: 2, Sjø: 2, Trafikk: 2, Naturfare: 2,
};
/**
 * Markører som LØFTER alvorligheten.
 *
 * ⚠ POLITIET SKRIVER NESTEN FORMELAKTIG, og det er derfor regelen er robust og
 * ikke skjør: «Ingen personskade», «livstruende», «brannen er slukket» er
 * operativ konvensjon, ikke fritt språk.
 */
const HEV_MARKORER = [
    'livstruende', 'livstruande', 'omkom', 'omkommet', 'død', 'døde', 'drept',
    'evakuer', 'kritisk skadet', 'kritisk skadd', 'alvorlig skadet', 'alvorlig skadd',
    'bevæpnet', 'skudd', 'eksplosjon',
];
/** Markører som SENKER alvorligheten. */
const SENK_MARKORER = [
    'ingen personskade', 'ingen skadet', 'ingen skadd', 'ingen er skadet',
    'brannen er slukket', 'slukket', 'mindre materielle skader',
    'ikke skadet', 'ikke skadd', 'funnet i god behold', 'løst seg',
];
/**
 * Vurder alvorlighet for en politimelding.
 *
 * Rekkefølge: kategori-basis → tekstmarkører justerer. ⚠ HEVING SLÅR SENKING. En
 * melding som sier både «brannen er slukket» og «én person omkom» er alvorlig, og
 * en regel som lot slukkingen vinne ville rangert et dødsfall som rutine.
 */
export function vurderAlvorlighet(kategori, tekst) {
    const basis = KATEGORI_BASIS[kategori] ?? 1;
    const t = tekst.toLowerCase();
    const hev = HEV_MARKORER.find((m) => t.includes(m));
    if (hev) {
        return {
            nivaa: 3, kilde: 'tekstmarkør', markor: hev,
            forklaring: `Hevet til 3 av markøren «${hev}» i politiets tekst`,
        };
    }
    const senk = SENK_MARKORER.find((m) => t.includes(m));
    if (senk) {
        const nivaa = Math.max(1, basis - 1);
        return {
            nivaa, kilde: 'tekstmarkør', markor: senk,
            forklaring: `Senket til ${nivaa} av markøren «${senk}» i politiets tekst`,
        };
    }
    if (KATEGORI_BASIS[kategori] !== undefined) {
        return { nivaa: basis, kilde: 'kategori', forklaring: `Basis ${basis} for kategorien «${kategori}»` };
    }
    // ⚠ `ukjent`, IKKE `kategori`. Kategorien har ingen basis, så tallet er en
    // standardverdi og ikke en vurdering — og det skal operatøren kunne se.
    return { nivaa: 1, kilde: 'ukjent', forklaring: `Ingen basis for «${kategori}» — satt til 1 som standard, ikke vurdert` };
}
// ── Presisjonsnivå ──────────────────────────────────────────────────────────
// ⚠ FLYTTET TIL `grunnbilde.ts` (2026-08-18). `Presisjon`, `PRESISJON_LABEL` og
// `presisjonsForbehold` er ikke politi-spesifikke: ETHVERT grunnbildeobjekt kan ha
// en usikker posisjon, og eierbeslutningen om at hendelser skal PÅ kartet med
// ærlig usikkerhet gjelder alle kilder. De re-eksporteres herfra så eksisterende
// import-steder ikke brytes.
export { PRESISJON_LABEL, presisjonsForbehold };
// ── Til grunnbildeobjekt ────────────────────────────────────────────────────
/** Trunker på setningsgrense, som planen krever. Aldri midt i et ord. */
export function utdragAv(tekst, maks = 300) {
    const t = tekst.trim().replace(/\s+/g, ' ');
    if (t.length <= maks)
        return t;
    const kuttet = t.slice(0, maks);
    // Siste setningsslutt innenfor vinduet — ellers siste ordgrense.
    const setning = Math.max(kuttet.lastIndexOf('. '), kuttet.lastIndexOf('! '), kuttet.lastIndexOf('? '));
    // ⚠ `>=`, IKKE `>`. Vakta på halve lengden finnes for at vi ikke skal vise 20
    // tegn når 300 var tilgjengelig. Men PRESIS på grensa er en hel setning bedre
    // enn en ellipse: en fullstendig setning fra politiet er alltid mer lesbar, og
    // aldri misvisende. Testen fanget dette — med `>` falt et utdrag med
    // setningsslutt på nøyaktig halvveis tilbake til ordgrense og ellipse.
    if (setning >= maks * 0.5)
        return kuttet.slice(0, setning + 1);
    const ord = kuttet.lastIndexOf(' ');
    return `${(ord > 0 ? kuttet.slice(0, ord) : kuttet).trim()} …`;
}
// ── Permalenke til den offentlige Politiloggen-siden ───────────────────────
//
// ⚠ VERIFISERT MOT LIVE API + RSS-FEED (2026-08-19), IKKE GJETTET. `PolitiMeldingSchema`
// (linje ~62) har ingen egen lenke — API-svaret gir ingen `url`/`link`-felt på en
// melding. Men politiets EGEN RSS-feed (`GET https://api.politiloggen.politiet.no/feeds/rss`)
// publiserer `<link>`/`<guid isPermaLink="true">` for hver melding i formatet
//   https://www.politiet.no/politiloggen/hendelse/{traadId}/{meldingsindeks}
// Kryssjekket mot `/messagethreads` (som har tråd-id `26cx61`) og `/messages`
// (der `MessageDto.id` er nøyaktig `{traadId}-{meldingsindeks}`, f.eks. `26cx61-0`,
// og `MessageDto.threadId` er `26cx61` i eget felt) bekreftet at RSS-lenkens to
// segmenter er nettopp disse — og en hentet lenke ga faktisk HTTP 200 med
// meldings-id-en i den returnerte siden.
//
// `PolitiMeldingSchema.id` KAN i praksis være to ting, avhengig av hvilket
// API-endepunkt kalleren fylte den fra:
//   1. Det flate `{traadId}-{indeks}`-formatet fra `/messages` — her har `m.id`
//      selv indeksen, og `m.threadId` er også satt. Dette er formatet hubben
//      FAKTISK bruker i dag (se `services/hub/src/politiloggen.ts` — rettet
//      2026-08-19, se filhodet punkt 2 for hva som var feil før).
//   2. Tråd-id-en ALENE, uten indeks — det som ville skjedd hvis en kaller sendte
//      inn et rått `/messagethreads`-tråd-objekt direkte (uten å pakke ut den
//      nestede `messages`-lista). Beholdt her som fallback, ikke fordi hubben
//      gjør dette lenger.
// Begge gir en GYLDIG lenke: i tilfelle 2 peker `/0` til trådens FØRSTE melding,
// som alltid finnes.
export function politiPermalink(m) {
    const flatt = /^([0-9a-z]{3,16})-(\d{1,4})$/i.exec(m.id.trim());
    if (flatt)
        return `https://www.politiet.no/politiloggen/hendelse/${flatt[1]}/${flatt[2]}`;
    const traadId = m.threadId?.trim() || m.id.trim();
    // ⚠ KUN NÅR TRÅD-ID-EN SER UT SOM DE VI HAR OBSERVERT (base36-aktig kode uten
    // bindestrek). En ukjent form skal IKKE bli en gjettet lenke — se filhodet punkt 3.
    if (/^[0-9a-z]{3,16}$/i.test(traadId)) {
        return `https://www.politiet.no/politiloggen/hendelse/${traadId}/0`;
    }
    return undefined;
}
/**
 * Kommune + fylke fra ETT kildefelt (kommunenavn, evt. + politidistrikt).
 *
 * ── HK-02 — GJENBRUK, IKKE NY GAZETTEER ─────────────────────────────────────
 * `HENDELSESKART_PLAN.md` §1 og kanban HK-02 siterer `kommuneKeys()`
 * (`hv-structure.ts:293`) som løsningen på tospråklig navnematch. Men da
 * kommune-oppslaget FAKTISK ble bygget for Politiloggen (denne fila,
 * `kommunenrFor` i `kommunenr.ts`), fikk det sin EGEN normalisering — bevisst
 * lokal (se filhodet i `kommunenr.ts`) OG strengt tatt mer robust enn
 * `kommuneKeys()`: den folder også tankestrek/minustegn til vanlig
 * bindestrek, noe `normKommune` i `hv-structure.ts` ikke gjør (se
 * kommentaren over `norm()` i `kommunenr.ts`). Den er allerede koblet inn i
 * `politiTilGrunnbilde()` og allerede grundig testet (`kommunenr.test.ts`).
 *
 * Å bygge denne funksjonen på `kommuneKeys()`/`kommuneAreaIndex()` i stedet
 * ville laget en ANDRE, SVAKERE navnematcher for samme jobb — nøyaktig den
 * doble modelleringen grunnlovens §3 forbyr. Denne funksjonen gjenbruker
 * derfor det produksjonssatte oppslaget (`kommunenrFor` + `kommunenavnFor`)
 * og henter fylke via `districtForKommuneNr()`, EKSAKT som notatet beskriver
 * for fylkesleddet.
 */
export function kommuneFylkeFraKildefelt(kommunenavn, politidistrikt) {
    const treff = kommunenrFor(kommunenavn, politidistrikt);
    if (!treff.knr)
        return {};
    return {
        kommuneId: treff.knr,
        kommuneNavn: kommunenavnFor(treff.knr),
        fylke: districtForKommuneNr(treff.knr),
    };
}
/**
 * Politimelding → grunnbildeobjekt.
 *
 * `posisjon` sendes INN fordi geokodingen krever nettverk (Kartverket SSR) og
 * hører i hubben. Uten posisjon blir objektet stående uten `lat`/`lon`, og det er
 * riktig: **en hendelse uten ærlig sted er bedre enn en hendelse på feil sted.**
 *
 * ── ⚠ TO LEVETIDER (EIERBESLUTNING 2026-08-18) ──────────────────────────────
 *   «I skade legges det i tidslinja, blir grått etter et døgn.» … «i GNÅ skal
 *   nyheten droppes etter en uke.»
 *
 * Derfor to tall, og forskjellen er hele poenget:
 *   • `POLITI_FORELDET_MS` = 1 døgn  — grå, men fortsatt der
 *   • `POLITI_LEVETID_MS`  = 1 uke   — borte fra det levende bildet
 *
 * ⚠ LEVETIDEN VAR 24 TIMER FØR DENNE BESLUTNINGEN, og det var for kort: en
 * savnetsak eller en stengt vei kan vare i tre dager, og en melding som forsvant
 * ved midnatt tok den pågående saken med seg. Nå står den ut uka — grå, med en
 * tekst som sier at kilden har vært stille, ikke at saken er over.
 *
 * ⚠ ARKIVET ER NOE TREDJE. Uka er hvor lenge meldingen er del av
 * SITUASJONSBILDET. Historikken bor i `observasjonsarkiv.ts` og har sin egen
 * oppbevaring — se `politiTilObservasjon()`.
 */
export const POLITI_FORELDET_MS = 24 * 60 * 60_000;
export const POLITI_LEVETID_MS = 7 * 24 * 60 * 60_000;
/** Attribusjonen NLOD 2.0 krever. Følger hvert objekt ut. */
export const POLITI_ATTRIBUSJON = 'Politiet (politiloggen.politiet.no) — NLOD 2.0';
export function politiTilGrunnbilde(m, hentetMs, posisjon) {
    const tsMs = Date.parse(m.createdOn);
    // ⚠ EN UTOLKELIG DATO GIR null, IKKE `Date.now()`. Å stemple meldingen med
    // «nå» ville gjort en gammel hendelse til en fersk — den verste enkeltfeilen i
    // et situasjonsbilde.
    if (!Number.isFinite(tsMs))
        return null;
    const kategori = kategoriFor(m.category);
    const alvorlighet = vurderAlvorlighet(kategori, m.text);
    const kommune = kommunenrFor(m.municipality, m.district);
    // ⚠ GJENBRUKER `kommune.knr` I STEDET FOR Å KALLE `kommunenrFor` PÅ NYTT.
    // Ett navnematch per melding, ikke to — og de to feltene kan derfor aldri
    // komme i utakt med hverandre.
    const kommuneFylke = kommune.knr
        ? { kommuneId: kommune.knr, kommuneNavn: kommunenavnFor(kommune.knr), fylke: districtForKommuneNr(kommune.knr) }
        : {};
    const presisjon = posisjon?.presisjon ?? 'ukjent';
    const stedsdeler = [m.area, m.municipality, m.district].filter((x) => x.trim());
    const forbehold = [
        presisjonsForbehold(presisjon),
        // ⚠ ALVORLIGHETENS PROVENIENS FØLGER OBJEKTET. Uten den er tallet en påstand
        // ingen kan overprøve.
        alvorlighet.forklaring,
    ].filter(Boolean).join(' · ');
    const objekt = GrunnbildeObjektSchema.parse({
        id: `politiloggen:${m.id}`,
        klasse: 'hendelse',
        type: kategori,
        // Tittelen er kildens egne felt satt sammen — ikke en generert overskrift.
        tittel: `${kategori}${stedsdeler.length ? ` — ${stedsdeler.join(', ')}` : ''}`,
        // ⚠ POLITIETS EGEN TEKST, BARE TRUNKERT. Se filhodet punkt 3.
        utdrag: utdragAv(m.text),
        ...(posisjon
            ? {
                lat: posisjon.lat,
                lon: posisjon.lon,
                presisjon: posisjon.presisjon,
                // ⚠ 0 BLIR undefined, IKKE 0. Et punkt har ingen usikkerhetssirkel, og
                // `usikkerhetM: 0` ville fått `skalTegneUsikkerhet` til å regne på et tall
                // som betyr «ingen sirkel» — riktig svar i dag, men et tall som inviterer
                // til å bli sammenlignet.
                ...(posisjon.usikkerhetM ? { usikkerhetM: posisjon.usikkerhetM } : {}),
                ...(posisjon.posisjonKilde ? { posisjonKilde: posisjon.posisjonKilde } : {}),
            }
            : { presisjon: 'ukjent' }),
        tsMs,
        mottattMs: hentetMs,
        levetidMs: POLITI_LEVETID_MS,
        foreldetMs: POLITI_FORELDET_MS,
        // Alvorlighet 1–3 → konfidens er IKKE det samme. Konfidensen her handler om
        // hvor sikkert vi vet at hendelsen ER: politiet er en høytillitskilde som
        // melder om noe de selv har vært på, så den er høy uansett alvorlighet.
        konfidens: 0.9,
        proveniens: {
            kildeId: 'politiloggen',
            kildeNavn: 'Politiloggen',
            lisens: 'NLOD 2.0 (Norsk lisens for offentlige data)',
            lisensStatus: 'avklart',
            hentetMs,
            // ⚠ PERMALENKE TIL DENNE MELDINGEN NÅR VI KAN BYGGE EN — se `politiPermalink()`.
            // Faller tilbake til den generiske Politiloggen-forsiden når id-formen ikke
            // kan tolkes trygt: en fungerende, men uspesifikk lenke er ærligere enn en
            // gjettet en som kan peke feil.
            url: politiPermalink(m) ?? 'https://www.politiet.no/politiloggen/',
        },
        forbehold,
    });
    return { objekt, kommune, kommuneFylke, presisjon, alvorlighet, kategori };
}
/**
 * Kommunesenter-oppslag finnes IKKE her, og det er med vilje.
 *
 * ⚠ Å legge et `KOMMUNE_SENTER`-oppslag i denne fila ville gjort det billig å
 * plassere hver hendelse på kommunesenteret og kalle det ferdig. Nivået `kommune`
 * krever både senterkoordinat OG en kartstil som tegner et skravert område framfor
 * en pin, og de to hører sammen. Kommer senteret uten stilen, har vi bygget
 * nettopp det kartet planen advarer mot.
 *
 * Funksjonen står her som en påminnelse for den neste som lurer på hvorfor
 * `politiTilGrunnbilde` returnerer objekter uten posisjon.
 */
export function hvorforIngenPosisjon(m) {
    const k = kommunenrFor(m.municipality, m.district);
    if (!m.municipality.trim())
        return 'Meldingen oppgir ingen kommune';
    if (!k.knr && k.kandidater.length > 1) {
        return `Kommunenavnet «${m.municipality}» er tvetydig (${k.kandidater.map((n) => kommunenavnFor(n) ?? n).join(', ')}) og politidistriktet skilte dem ikke`;
    }
    if (!k.knr)
        return `Kommunenavnet «${m.municipality}» finnes ikke i kommuneregisteret`;
    if (!m.area.trim())
        return `Kommunen er kjent (${kommunenavnFor(k.knr) ?? k.knr}), men meldingen oppgir ingen område-tekst å geokode`;
    return `Kommunen er kjent (${kommunenavnFor(k.knr) ?? k.knr}), men område-teksten «${m.area}» ga ingen entydig treff i stedsnavnregisteret`;
}
// ── ARKIVERING (eierbeslutning 2026-08-18: «ja» til arkiv) ──────────────────
/**
 * Politimelding → arkivert observasjon.
 *
 * ── ⚠ TRE AVGRENSNINGER SOM MÅ STÅ ──────────────────────────────────────────
 *
 * 1. TYPEN ER `apen-kilde`, IKKE `spor`. En analytiker som søker i arkivet må
 *    kunne skille det VI har observert fra det vi har LEST et sted. En
 *    politimelding er politiets observasjon, gjengitt av oss. Blandet med egen
 *    innhenting kan et etterretningsprodukt komme til å sitere en åpen kilde som om
 *    det var vår egen — og `buildIntelPackage` skiller ikke selv.
 *
 * 2. KREVER POSISJON. Arkivet er geografisk søkbart (`Boks`, dekningshull,
 *    mønster), og en observasjon uten `lat`/`lon` finnes ikke i det. En hendelse
 *    uten kjent kommune arkiveres derfor IKKE — den står i tidslinja sin uke og
 *    forsvinner. ⚠ Å arkivere den på 0,0 for å få den inn ville lagt en
 *    politimelding i Guineabukta og gjort dekningsanalysen usann.
 *
 * 3. ⚠ GDPR ER IKKE LØST AV DENNE FUNKSJONEN, OG DET SKAL SIES HØYT.
 *    En politimelding handler om identifiserbare mennesker. Sted + tid + hendelse
 *    kan peke ut en person i et lite lokalsamfunn selv uten navn, og et PERMANENT
 *    SØKBART arkiv over dem er en helt annen behandling enn et situasjonsbilde som
 *    tømmes etter en uke. NLOD 2.0 gir oss lov til å BRUKE dataene; den avgjør ikke
 *    hvor lenge vi kan lagre personopplysninger utledet av dem.
 *
 *    Funksjonen setter derfor `extra.personopplysninger = 'ja'` på hver post, slik
 *    at oppbevaringsregelen kan håndheves ETT sted når eier eller en jurist har
 *    satt den — og slik at ingen kan si de ikke visste. Se `OSINT-12` og `OSINT-21`.
 */
export function politiTilObservasjon(o, m) {
    // Krav 2: ingen posisjon → ingen arkivpost.
    if (o.lat === undefined || o.lon === undefined)
        return null;
    const obs = {
        id: `arkiv:${o.id}`,
        tsMs: o.tsMs,
        mottattMs: o.mottattMs,
        kind: 'apen-kilde',
        kilde: 'politiloggen',
        lat: o.lat,
        lon: o.lon,
        label: o.tittel,
        // ⚠ USIKKERHETEN BLIR `cepM`. Arkivet har allerede et felt for
        // posisjonsusikkerhet, og en estimert politihendelse med 30 km usikkerhet skal
        // ikke ligge i arkivet som om den var stedfestet — en analytiker som søker i en
        // boks på 2 km ville ellers fått treff hun ikke kan bruke.
        ...(o.usikkerhetM !== undefined ? { cepM: o.usikkerhetM } : {}),
        extra: {
            kategori: m.category || o.type,
            kommune: m.municipality,
            distrikt: m.district,
            omraade: m.area,
            // Proveniensen følger posten. Uten lisensen i arkivet kan en eksport dra
            // dataene ut uten vilkårene som gjelder for dem.
            lisens: o.proveniens.lisens,
            presisjon: o.presisjon ?? 'ukjent',
            posisjonKilde: o.posisjonKilde ?? 'ukjent',
            // ⚠ MERKET, IKKE LØST. Se avgrensning 3 i doc-kommentaren.
            personopplysninger: 'ja',
            ...(o.forbehold ? { forbehold: o.forbehold } : {}),
            ...(o.flyttetAv ? { posisjonRettetAv: o.flyttetAv.av } : {}),
            ...(o.proveniens.url ? { url: o.proveniens.url } : {}),
        },
    };
    return obs;
}
//# sourceMappingURL=politiloggen.js.map